import React, { useEffect, useState } from 'react';
import { Container, ButtonGroup, Button, Card, Row, Col, Table, Form } from 'react-bootstrap';
import { db } from '../firebase';
import BlogPostEditor from '../components/NewPost';
import EditPost from '../components/EditPost';
import { collection, addDoc, getDocs, doc, deleteDoc } from 'firebase/firestore';

const Dashboard = () => {
  const [view, setView] = useState('messages');
  const [post, setPost] = useState({
    title: '',
    content: ''
  });
  const [messages, setMessages] = useState([]);
  const [posts, setPosts] = useState([]);
  const [editingPost, setEditingPost] = useState(null);

  const handleEditPost = (post) => {
    setEditingPost(post);
  };

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const messagesCol = collection(db, 'messages');
        const messagesSnapshot = await getDocs(messagesCol);
        const messagesList = messagesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setMessages(messagesList);
      } catch (err) {
        console.error("Error fetching messages: ", err);
      }
    };    

    const fetchPosts = async () => {
      try {
        const postsCol = collection(db, 'posts');
        const postSnapshot = await getDocs(postsCol);
        const postsList = postSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setPosts(postsList);
      } catch (err) {
        console.error("Error fetching posts: ", err);
      }
    };

    fetchMessages();
    fetchPosts();
  }, []);

  const handleChange = (e) => {
    setPost({
      ...post,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const postsCol = collection(db, 'posts');
    await addDoc(postsCol, post);
    console.log('Post added!');
    setPost({ title: '', content: '' });
  };

  const handleDeleteMessage = async (id) => {
    try {
      const messageRef = doc(db, 'messages', id);
      await deleteDoc(messageRef);
      console.log('Message deleted!');
      const messagesCol = collection(db, 'messages');
      const messagesSnapshot = await getDocs(messagesCol);
      const messagesList = messagesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMessages(messagesList);
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const handleReplyMessage = (email) => {
    window.location.href = `mailto:${email}`;
  };

  const handleDeletePost = async (id) => {
    try {
      const postRef = doc(db, 'posts', id);
      await deleteDoc(postRef);
      console.log('Post deleted!');
      const postsCol = collection(db, 'posts');
      const postSnapshot = await getDocs(postsCol);
      const postsList = postSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPosts(postsList);
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  const getPostContentSnippet = (content) => {
    // Strip HTML tags and get plain text
    const plainText = content.replace(/<\/?[^>]+(>|$)/g, "");
    // Truncate to desired length
    return plainText.length > 100 ? plainText.substring(0, 100) + '...' : plainText;
  };

  return (
    <Container className="mt-5 min-vh-100">
      {!editingPost && (
      <Row>
        <Col>
          <ButtonGroup aria-label="Dashboard view toggle">
            <Button variant="secondary" onClick={() => setView('messages')} active={view === 'messages'}>Inbox</Button>
            <Button variant="secondary" onClick={() => setView('blog')} active={view === 'blog'}>New Post</Button>
            <Button variant="secondary" onClick={() => setView('oldPosts')} active={view === 'oldPosts'}>Old Posts</Button>
          </ButtonGroup>
        </Col>
      </Row>
      )}
      <Row className="mt-3">
        <Col>
          {editingPost ? (
                      <EditPost 
                      editingPost={editingPost} 
                      setEditingPost={setEditingPost} 
                      setPosts={setPosts}
                    />
          ) : (
            <>
              {view === 'messages' && (
                <Card className="p-4 mb-4">
                  <h2>Messages</h2>
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>First Name</th>
                        <th>Last Name</th>
                        <th>Email</th>
                        <th>Message</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {messages.map(message => (
                        <tr key={message.id}>
                          <td>{message.firstName}</td>
                          <td>{message.lastName}</td>
                          <td>{message.email}</td>
                          <td>{message.message}</td>
                          <td>
                            <Button variant="danger" size="sm" onClick={() => handleDeleteMessage(message.id)}>Delete</Button>{' '}
                            <Button variant="info" size="sm" onClick={() => handleReplyMessage(message.email)}>Reply</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card>
              )}
              {view === 'blog' && (
                <Card className="p-4">
                  <h2>Create a New Blog Post</h2>
                  <Form onSubmit={handleSubmit}>
                    <Form.Group controlId="postTitle">
                      <Form.Label>Title</Form.Label>
                      <Form.Control 
                        type="text" 
                        placeholder="Enter post title" 
                        name="title" 
                        value={post.title} 
                        onChange={handleChange} 
                        required 
                      />
                    </Form.Group>
                    <Form.Group controlId="postContent">
                      <Form.Label>Content</Form.Label>
                      <BlogPostEditor content={post.content} setContent={(value) => setPost(prev => ({ ...prev, content: value }))} />
                    </Form.Group>
                    <Button id='x2' variant="primary" type="submit">Submit</Button>
                  </Form>
                </Card>
              )}
              {view === 'oldPosts' && (
                <Card className="p-4 mb-4">
                  <h2>Old Posts</h2>
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Content</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {posts.map(post => (
                        <tr key={post.id}>
                          <td>{post.title}</td>
                          <td className="post-content-preview">
                            {getPostContentSnippet(post.content)}
                          </td>
                          <td>
                            <Button variant="danger" size="sm" onClick={() => handleDeletePost(post.id)}>Delete</Button>
                            <Button variant="info" size="sm" onClick={() => handleEditPost(post)}>Edit</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card>
              )}
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default Dashboard;
